import { createContext, useContext, useState, useEffect } from 'react';

// ∑n-route defaults — new platform default (was GuruSkool)
const ENROUTE_BRANDING = {
  platform_name: "∑n-route",
  platform_display_name: "∑n-route",
  platform_plain_name: "nroute",
  platform_short_name: "∑n-route",
  tagline: "Your global north star",
  tagline_compact: "AI-powered study abroad",
  logo_url: null,  // Uses inline SVG constellation component
  logo_icon_url: null,
  primary_color: "#0B2540",      // Deep Navy
  secondary_color: "#E8A838",    // North Star Gold
  accent_color: "#4BA3C3",       // Ocean Teal
  sigma_color: "#E8A838",        // Sigma symbol color
  path_color: "#4BA3C3",         // Constellation path color
  hyphen_color: "#4BA3C3",       // Wordmark hyphen color
  surface_color: "#0F172A",      // Dark mode surface
  background_color: "#020617",   // Dark mode background
  surface_light: "#F8FAFC",      // Light mode surface
  background_light: "#FFFFFF",   // Light mode background
  default_theme: "dark",
  allow_theme_toggle: false,  // ∑n-route is dark-mode-only
};

// Legacy GuruSkool config (kept for backwards compatibility)
const PLATFORM_DEFAULTS = {
  platform_name: "GuruSkool",
  platform_short_name: "GuruSkool",
  tagline: "AI-powered program discovery",
  logo_url: null,              // Will use inline SVG fallback
  logo_icon_url: null,
  primary_color: "#0EA5E9",    // Sky blue — platform neutral
  secondary_color: "#22C55E",  // Green — platform neutral
  surface_color: "#0F172A",    // Dark mode surface
  background_color: "#020617", // Dark mode background
  surface_light: "#F8FAFC",    // Light mode surface
  background_light: "#FFFFFF", // Light mode background
  default_theme: "dark",
  allow_theme_toggle: true,
};

// EazyLink config — hardcoded for Phase 1, will come from API in later phase
const EAZYLINK_BRANDING = {
  platform_name: "EazyCourseEngine",
  platform_short_name: "EazyCourseEngine",
  tagline: "Your AI-powered course finder",
  logo_url: null,              // Uses inline SVG globe component
  logo_icon_url: null,
  primary_color: "#0060B0",    // EazyLink blue
  secondary_color: "#F06020",  // EazyLink orange
  surface_color: "#0F172A",
  background_color: "#020617",
  surface_light: "#F8FAFC",
  background_light: "#FFFFFF",
  default_theme: "dark",
  allow_theme_toggle: true,
};

const BrandingContext = createContext(ENROUTE_BRANDING);

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(ENROUTE_BRANDING);

  useEffect(() => {
    const applyBrandingFromHostname = () => {
      // DEV-ONLY: Query param tenant override for local testing
      // Usage: ?tenant=ece or ?tenant=nroute
      // Strictly gated — never runs in production
      if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const tenantOverride = urlParams.get('tenant');

        if (tenantOverride === 'ece') {
          console.info('[BrandingContext] DEV override → ECE tenant');
          setBranding(EAZYLINK_BRANDING);
          applyCSS(EAZYLINK_BRANDING);
          return;
        }
        if (tenantOverride === 'nroute') {
          console.info('[BrandingContext] DEV override → NRoute tenant');
          setBranding(ENROUTE_BRANDING);
          applyCSS(ENROUTE_BRANDING);
          return;
        }
      }

      const hostname = window.location.hostname;

      // Priority 1: Hostname detection (synchronous, no flash)
      if (hostname.includes('nroute.ai')) {
        console.log('[BrandingContext] ∑n-route hostname detected → Enroute branding');
        setBranding(ENROUTE_BRANDING);
        applyCSS(ENROUTE_BRANDING);
        return;
      }

      // Priority 2: guruskool.tmplabs.ai → always EazyCourseEngine (pre-login and post-login)
      if (hostname.includes('guruskool.tmplabs.ai')) {
        console.log('[BrandingContext] guruskool.tmplabs.ai hostname → EazyCourseEngine branding');
        setBranding(EAZYLINK_BRANDING);
        applyCSS(EAZYLINK_BRANDING);
        return;
      }

      // Priority 3: Everything else → ∑n-route (new platform default)
      console.log('[BrandingContext] Default → ∑n-route branding');
      setBranding(ENROUTE_BRANDING);
      applyCSS(ENROUTE_BRANDING);
    };

    // Apply CSS custom properties to document root
    const applyCSS = (brandingConfig) => {
      const root = document.documentElement;
      root.style.setProperty('--brand-primary', brandingConfig.primary_color);
      root.style.setProperty('--brand-secondary', brandingConfig.secondary_color);
      root.style.setProperty('--brand-surface', brandingConfig.surface_color);
      root.style.setProperty('--brand-bg', brandingConfig.background_color);
      root.style.setProperty('--brand-surface-light', brandingConfig.surface_light);
      root.style.setProperty('--brand-bg-light', brandingConfig.background_light);

      if (brandingConfig.accent_color) {
        root.style.setProperty('--brand-accent', brandingConfig.accent_color);
      }

      // Set document title based on branding
      document.title = brandingConfig.platform_name === '∑n-route'
        ? '∑n-route | Your global north star'
        : `${brandingConfig.platform_name} | ${brandingConfig.tagline}`;
    };

    // Run on mount (handles page refresh with existing localStorage)
    applyBrandingFromHostname();

    // Re-run when user logs in, logs out, or switches tenant
    window.addEventListener('auth-user-updated', applyBrandingFromHostname);

    return () => {
      window.removeEventListener('auth-user-updated', applyBrandingFromHostname);
    };
  }, []);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

export { PLATFORM_DEFAULTS, EAZYLINK_BRANDING, ENROUTE_BRANDING };
